import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MockPlayer from "./components/MockPlayer";
import EpisodeList from "./components/EpisodeList";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/base/EmptyState";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";
import { animeList } from "@/mocks/anime";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const anime = useMemo(() => animeList.find((a) => a.id === id), [id]);
  const [episode, setEpisode] = useState(1);
  const [toast, setToast] = useState("");
  const { toggleFavorite, isFavorite } = useFavorites();

  useSeo(
    anime ? `${anime.title} - 樱花动漫在线` : "动漫详情 - 樱花动漫在线",
    anime ? anime.description.slice(0, 80) : "动漫详情页面"
  );

  useEffect(() => {
    setEpisode(1);
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => {
    if (!anime) return [];
    return animeList
      .filter((a) => a.id !== anime.id)
      .map((a) => ({
        anime: a,
        score: a.categories.filter((c) => anime.categories.includes(c)).length,
      }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score || b.anime.views - a.anime.views)
      .slice(0, 4)
      .map((x) => x.anime);
  }, [anime]);

  if (!anime) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-file-unknow-line"
          title="该动漫信息暂缺"
          description="没有找到对应的动漫资料，可能是链接已失效或内容已下架。"
        />
        <div className="flex justify-center">
          <Link
            to="/category"
            className="px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回分类页
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(anime.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      }
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  const handleFavorite = () => {
    const added = toggleFavorite(anime.id);
    setToast(added ? "已加入收藏夹" : "已从收藏夹移除");
  };

  const infoItems = [
    { label: "类型", value: anime.categories.join(" / ") },
    { label: "导演", value: anime.director },
    { label: "年份", value: `${anime.year} 年` },
    { label: "集数", value: `共 ${anime.episodes} 集` },
    { label: "状态", value: anime.status },
    { label: "地区", value: anime.region },
  ];

  return (
    <div className="w-full">
      <div className="w-full bg-primary-50 border-b border-primary-100">
        <div className="w-full px-4 md:px-8 py-6">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-600 flex-wrap">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/category" className="hover:text-primary-700 cursor-pointer">
              动漫分类
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/category?category=${encodeURIComponent(anime.categories[0])}`}
              className="hover:text-primary-700 cursor-pointer"
            >
              {anime.categories[0]}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900 font-medium">{anime.title}</span>
          </nav>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row gap-5 md:gap-6">
              <div className="w-40 sm:w-52 shrink-0 mx-auto sm:mx-0">
                <div className="w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-100 border border-background-200">
                  <img
                    src={anime.cover}
                    alt={`${anime.title} 动漫封面`}
                    title={`${anime.title} 动漫在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                    {anime.status}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
                    {anime.rating.toFixed(1)} 分
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                    {anime.latestEpisode}
                  </span>
                </div>

                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3 leading-snug">
                  {anime.title}
                </h1>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {anime.categories.map((c) => (
                    <Link
                      key={c}
                      to={`/category?category=${encodeURIComponent(c)}`}
                      className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-700 text-xs hover:bg-primary-100 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {c}
                    </Link>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mb-5">
                  {infoItems.map((item) => (
                    <div key={item.label} className="text-xs md:text-sm">
                      <span className="text-foreground-500">{item.label}：</span>
                      <span className="text-foreground-900 font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      favorited
                        ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                        : "bg-background-100 text-foreground-800 hover:bg-primary-100 hover:text-primary-800"
                    }`}
                  >
                    <i className={favorited ? "ri-heart-fill text-lg" : "ri-heart-line text-lg"}></i>
                    {favorited ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-share-forward-line text-lg"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="text-base md:text-lg font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-file-text-line text-primary-600"></i>
                剧情简介
              </h2>
              <div className="prose-anime text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>{anime.description}</p>
                <p>
                  本作为演示内容，剧情、人物与数据均为虚构示例，用于展示樱花动漫在线的内容浏览与播放体验。
                </p>
              </div>
            </div>

            <div className="mt-8 space-y-5">
              <MockPlayer anime={anime} episode={episode} />
              <EpisodeList total={anime.episodes} current={episode} onSelect={setEpisode} />
            </div>
          </div>

          <aside className="w-full lg:w-[320px] shrink-0">
            <div className="lg:sticky lg:top-24 space-y-5">
              <div className="rounded-lg bg-background-50 border border-background-200 p-5">
                <h2 className="text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-bookmark-3-line text-accent-600"></i>
                  相关推荐
                </h2>
                {related.length === 0 ? (
                  <p className="text-sm text-foreground-500">暂无相关推荐</p>
                ) : (
                  <div className="flex flex-col divide-y divide-background-200">
                    {related.map((r) => (
                      <Link
                        key={r.id}
                        to={`/detail/${r.id}`}
                        className="group flex gap-3 py-3 cursor-pointer"
                      >
                        <div className="w-14 h-20 rounded-md overflow-hidden bg-background-100 shrink-0">
                          <img
                            src={r.cover}
                            alt={`${r.title} 封面`}
                            title={`${r.title} 动漫在线观看`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground-950 line-clamp-1 group-hover:text-primary-700 transition-colors">
                            {r.title}
                          </p>
                          <p className="text-xs text-foreground-500 mt-1 line-clamp-2 leading-relaxed">
                            {r.description}
                          </p>
                          <span className="inline-block mt-1.5 text-[11px] text-accent-600 font-semibold">
                            {r.rating.toFixed(1)} 分 · {r.status}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg bg-primary-50 border border-primary-100 p-5">
                <h3 className="text-sm font-bold text-foreground-950 mb-2">按分类继续浏览</h3>
                <div className="flex flex-wrap gap-2">
                  {anime.categories.map((c) => (
                    <Link
                      key={c}
                      to={`/category?category=${encodeURIComponent(c)}`}
                      className="px-3 py-1.5 rounded-full bg-background-50 border border-primary-200 text-xs text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {c}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5">相似动漫推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((r) => (
                <AnimeCard key={r.id} anime={r} showDescription={false} />
              ))}
            </div>
          </div>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm animate-fadeIn">
          <i className="ri-checkbox-circle-line mr-2"></i>
          {toast}
        </div>
      )}
    </div>
  );
}